import type { StateCreator } from 'zustand'
import type { loadingState } from './loadingSlice'
import type { authSessionState } from './authSessionSlice'
import type { BoardSliceState } from './boardSlice'

import { apiService } from '@/service/api'
import type { ChatConversation, Message, MessageRole } from '@/service/api'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

export const ROLES = {
  assistant: 'assistant',
  user: 'user',
}

export interface ChatState {
  chatMessages: Message[]
  context_id: string | null
  chatConversations: ChatConversation[]
  currentConversationId: string | null
  activeAgent: string
  // When true, chat retrieval covers the whole data catalog, not just the
  // board's docs. Sent as `entire_data_catalog`. Session-only.
  entireDataCatalog: boolean
  isChatLoading: boolean
  /**
   * True while `selectConversation` is fetching messages for the current
   * conversationId. Used by the chat UI to avoid flashing the empty-state
   * "agent logo" between the moment a conversation gets selected and the
   * moment its messages arrive.
   */
  messagesLoading: boolean
  stopHandlers: Record<string, () => void>
  inputValue: string
  hasStartedChatting: boolean
  isCheckingConversations: boolean
  /**
   * Document IDs the user has explicitly attached to the chat scope from
   * the board (kebab → "Add to Chat"). Renders as removable chips above
   * the textarea so the backend can narrow the AI search-space to just
   * these docs. Empty array means "use whatever's already on the board".
   */
  chatContextDocIds: string[]
  /**
   * Counter incremented whenever the user explicitly requests the chat
   * panel to open from outside the chat panel itself — e.g. "Add to Chat"
   * on a board card kebab, or "Add N to Chat" from the right-click
   * context menu. BoardCanvas watches this nonce in an effect and flips
   * its local chatOpen + entryDismissed state. A counter (rather than a
   * boolean) so re-adding a doc while the panel is already open still
   * counts as a fresh request — useful for surfacing the panel after
   * the user has minimized it.
   */
  chatOpenRequestNonce: number
  // Actions
  globalStopResponse: () => void

  addDocToChatContext: (docId: string) => void
  addDocsToChatContext: (docIds: string[]) => void
  removeDocFromChatContext: (docId: string) => void
  clearChatContext: () => void
  requestOpenChat: () => void

  setChatMessages: (
    updater: Message[] | ((prev: Message[]) => Message[])
  ) => void
  addChatMessage: (message: Message) => void

  setChatConversations: (
    updater:
      | ChatConversation[]
      | ((prev: ChatConversation[]) => ChatConversation[])
  ) => void

  setCurrentConversationId: (id: string | null) => void

  setActiveAgent: (agent: string) => void

  setEntireDataCatalog: (value: boolean) => void

  setIsChatLoading: (isChatLoading: boolean) => void

  setMessagesLoading: (messagesLoading: boolean) => void

  loadChatConversations: () => Promise<void>

  renameConversation: (id: string, title: string) => Promise<void>

  selectConversation: (id: string, expectedContextId?: string) => Promise<void>

  sendUserMessage: (
    input: string,
    contextType: 'workspace' | 'document'
  ) => Promise<{ conversationId: string; messageId: string }>

  registerStopHandler: (messageId: string, handler: () => void) => void
  unregisterStopHandler: (messageId: string) => void
  setGlobalStopResponse: (fn: () => void) => void
  stopReply: (messageId: string) => void
  setInputValue: (message: string) => void
  resetChatState: () => void
  setContextId: (context_id: string | null) => void
  checkHasConversations: () => Promise<void>
}

export const initialChatState = {
  chatMessages: [],
  context_id: null,
  chatConversations: [],
  currentConversationId: null,
  activeAgent: 'plant_support_engineer',
  entireDataCatalog: false,
  isChatLoading: false,
  messagesLoading: false,
  stopHandlers: {},
  inputValue: '',
  hasStartedChatting: false,
  isCheckingConversations: true,
  chatContextDocIds: [],
  chatOpenRequestNonce: 0,
  globalStopResponse: () => {},
}

export const chatSlice: StateCreator<
  ChatState & loadingState & authSessionState & BoardSliceState,
  [],
  [],
  ChatState
> = (set, get) => ({
  ...initialChatState,

  setChatMessages: (updater) =>
    set((state) => ({
      chatMessages:
        typeof updater === 'function' ? updater(state.chatMessages) : updater,
    })),

  addChatMessage: (message) =>
    set((state) => ({
      chatMessages: [...state.chatMessages, message],
    })),

  setChatConversations: (updater) =>
    set((state) => ({
      chatConversations:
        typeof updater === 'function'
          ? updater(state.chatConversations)
          : updater,
    })),

  setCurrentConversationId: (id) => set({ currentConversationId: id }),

  setActiveAgent: (agent) => set({ activeAgent: agent }),

  setEntireDataCatalog: (value) => set({ entireDataCatalog: value }),

  setIsChatLoading: (isChatLoading) => set({ isChatLoading }),

  setMessagesLoading: (messagesLoading) => set({ messagesLoading }),

  renameConversation: async (id, title) => {
    const trimmed = title.trim()
    if (!trimmed) return
    const { setChatConversations } = get()
    // Optimistically update just this conversation's title; roll back only this
    // field on failure. Snapshotting/restoring the whole array would clobber any
    // concurrent create/delete/message-update that lands during the request.
    const previousTitle = get().chatConversations.find((conv) => conv.id === id)?.title
    setChatConversations((prev) =>
      prev.map((conv) => (conv.id === id ? { ...conv, title: trimmed } : conv))
    )
    try {
      // Reconcile with the server's stored value (it may trim/normalize the
      // title) so optimistic state doesn't silently diverge until the next load.
      const updated = await apiService.renameConversation(id, trimmed)
      setChatConversations((prev) =>
        prev.map((conv) =>
          conv.id === id
            ? { ...conv, title: updated.title ?? trimmed, updated_at: updated.updated_at ?? conv.updated_at }
            : conv
        )
      )
    } catch (error) {
      console.error('Failed to rename conversation:', error)
      setChatConversations((prev) =>
        prev.map((conv) => (conv.id === id ? { ...conv, title: previousTitle } : conv))
      )
      toast.error('Failed to rename chat')
    }
  },

  loadChatConversations: async () => {
    const { setIsChatLoading, setChatConversations, context_id, selectConversation } = get()

    if (!context_id) return

    // Capture the context_id at the start to detect board switches during async operations
    const requestContextId = context_id

    setIsChatLoading(true)

    try {
      const chatConversations = await apiService.getConversationsByContext(
        requestContextId
      )

      // Check if user switched boards during the fetch - if so, discard stale results
      if (get().context_id !== requestContextId) {
        return
      }

      // Only filter out conversations without valid IDs, but keep those with empty messages
      const validConversations = chatConversations.filter(
        conv => conv && conv.id && conv.id.trim() !== ''
      )
      setChatConversations(validConversations)

      // Check if user had an unsent draft in "new chat" mode before navigating away
      // If so, restore "new chat" mode instead of auto-selecting a conversation
      try {
        const newChatDraftKey = `chatDraft_${requestContextId}_new`
        const newChatDraft = localStorage.getItem(newChatDraftKey)
        if (newChatDraft) {
          // User was in "new chat" mode with an unsent draft - stay in that mode
          // The ChatInterface component will restore the draft text from localStorage
          setIsChatLoading(false)
          return
        }
      } catch {
        // Ignore localStorage errors (e.g., Safari private browsing, quota exceeded)
      }

      // Auto-restore the last selected conversation for this board
      if (validConversations.length > 0) {
        // Check localStorage for the last selected conversation for this board
        const lastSelectedKey = `lastConversation_${requestContextId}`
        const lastSelectedId = localStorage.getItem(lastSelectedKey)

        // If user explicitly chose "new chat" mode (via Back to Recent), respect that
        // preference and stay in new chat mode instead of auto-selecting a conversation
        if (lastSelectedId === 'new') {
          setIsChatLoading(false)
          return
        }

        // Find if the last selected conversation still exists
        const lastConversation = lastSelectedId
          ? validConversations.find(conv => conv.id === lastSelectedId)
          : null

        // Select the last used conversation, or fall back to the most recent one
        const conversationToSelect = lastConversation || validConversations[0]
        if (conversationToSelect) {
          // Re-check context_id before selecting conversation to prevent race condition
          if (get().context_id !== requestContextId) {
            return
          }
          // Pass requestContextId to selectConversation to ensure it validates against
          // the original board context, preventing race conditions during rapid switching
          await selectConversation(conversationToSelect.id, requestContextId)
        }
      }
    } catch (error) {
      // Only show error if still on the same board
      if (get().context_id === requestContextId) {
        toast.error(`Failed to load conversations: ${error}`)
      }
    } finally {
      // Only clear loading state if still on the same board
      if (get().context_id === requestContextId) {
        setIsChatLoading(false)
      }
    }
  },

  selectConversation: async (id: string, expectedContextId?: string) => {
    const {
      currentConversationId,
      setCurrentConversationId,
      setChatMessages,
      setMessagesLoading,
      setActiveAgent,
      chatConversations,
      context_id,
    } = get()

    const { assistant } = ROLES

    // Capture the context we're operating on. Use expectedContextId if provided (from loadChatConversations)
    // to prevent race conditions during rapid board switching. Otherwise, use current context_id.
    const originalContextId = expectedContextId ?? context_id

    // Validate we're still on the expected board before proceeding
    // This prevents stale operations from affecting the wrong board
    if (get().context_id !== originalContextId) {
      return
    }

    if (id === currentConversationId) return

    // Clear stale messages and flag the conversation as loading so the chat
    // UI can suppress the empty-state agent logo while we fetch. Setting both
    // synchronously avoids the brief moment where `currentConversationId` is
    // set but `chatMessages` still holds the previous conversation's data.
    setChatMessages([])
    setMessagesLoading(true)
    setCurrentConversationId(id)

    // Persist the selected conversation for this board in localStorage
    // Use originalContextId to ensure we write to the correct board's key
    if (originalContextId) {
      try {
        const lastSelectedKey = `lastConversation_${originalContextId}`
        localStorage.setItem(lastSelectedKey, id)
      } catch {
        // Ignore localStorage errors (e.g., Safari private browsing, quota exceeded)
      }
    }

    // Update active agent from conversation data
    const conversation = chatConversations.find(conv => conv.id === id)
    if (conversation?.active_agent) {
      setActiveAgent(conversation.active_agent)
    }

    try {
      const messages = await apiService.getConversationMessages(id)

      // Check if user switched boards or conversations during the fetch - discard stale results
      if (get().context_id !== originalContextId || get().currentConversationId !== id) {
        return
      }

      // Handle empty or invalid response
      if (!messages || messages.length === 0) {
        setChatMessages([])
        return
      }

      messages.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )

      const orderedMessages: Message[] = []
      let lastMessage: Message | null = null

      for (const msg of messages) {
        if (
          lastMessage &&
          lastMessage.role === assistant &&
          msg.role === assistant
        ) {
          lastMessage.content += `\n\n${msg.content}`
        } else {
          if (lastMessage) orderedMessages.push(lastMessage)
          lastMessage = { ...msg }
        }
      }

      if (lastMessage) orderedMessages.push(lastMessage)

      setChatMessages(orderedMessages)
    } catch (error) {
      // Only update state if still on the same board and conversation
      if (get().context_id === originalContextId && get().currentConversationId === id) {
        // Still set the conversation as current even if messages fail to load
        setChatMessages([])
        // Don't show error toast for empty conversations, only for real errors
        if (error && !error.toString().includes('404')) {
          toast.error(`Failed to load conversation: ${error}`)
        }
      }
    } finally {
      // Always clear the loading flag once the fetch settles, even on stale
      // responses, so the chat UI can fall back to its empty-state render.
      if (get().currentConversationId === id) {
        setMessagesLoading(false)
      }
    }
  },

  sendUserMessage: async (
    input: string,
    contextType: 'workspace' | 'document'
  ): Promise<{ conversationId: string; messageId: string }> => {
    const {
      currentConversationId,
      context_id,
      setCurrentConversationId,
      setChatConversations,
      addChatMessage,
      setIsChatLoading,
    } = get()

    if (!context_id) {
      throw new Error('context_id is not defined')
    }

    let activeConversationId = currentConversationId

    try {
      if (!currentConversationId) {
        const response = await apiService.createConversation({
          context_id: context_id,
          context_type: contextType,
        })

        activeConversationId = response.conversation_id
        setCurrentConversationId(response.conversation_id)

        // Persist new conversation to localStorage so back navigation restores it
        // Also clear any "new chat" draft since we're now in a real conversation
        if (context_id) {
          try {
            localStorage.setItem(`lastConversation_${context_id}`, response.conversation_id)
            localStorage.removeItem(`chatDraft_${context_id}_new`)
          } catch {
            // Ignore localStorage errors (e.g., Safari private browsing, quota exceeded)
          }
        }

        setChatConversations((prev) => [
          {
            id: response.conversation_id,
            context_id: context_id,
            context_type: contextType,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            first_message: input,  // Keep original even if empty, will be handled by UI
            active_agent: get().activeAgent || 'plant_support_engineer',
          },
          ...prev,
        ])
        // Hide Getting Started panel immediately after first conversation
        set({ hasStartedChatting: true })
      }

      const messageId = uuidv4()

      const userMessage: Message = {
        id: messageId,
        conversation_id: activeConversationId!,
        role: ROLES.user as MessageRole,
        content: input,
        timestamp: new Date().toISOString(),
      }

      addChatMessage(userMessage)

      return { conversationId: activeConversationId!, messageId }
    } catch (error) {
      const errorMessage: Message = {
        id: uuidv4(),
        conversation_id: currentConversationId!,
        role: ROLES.assistant as MessageRole,
        content: 'Sorry, I encountered an error processing your request.',
        timestamp: new Date().toISOString(),
        error: `Failed to send message: ${error}`,
      }

      addChatMessage(errorMessage)
      setIsChatLoading(false)

      throw error
    }
  },
  // TODO: These are maintained per streaming response, but need to further refactor if this is not going to be the case
  registerStopHandler: (messageId, handler) =>
    set((state) => ({
      stopHandlers: {
        ...state.stopHandlers,
        [messageId]: handler,
      },
    })),

  unregisterStopHandler: (messageId) =>
    set((state) => {
      const stopHandlersCopy = { ...state.stopHandlers }
      delete stopHandlersCopy[messageId]
      return { stopHandlers: stopHandlersCopy }
    }),

  setGlobalStopResponse: (fn) => set({ globalStopResponse: fn }),

  stopReply: (messageId) => {
    const { stopHandlers, globalStopResponse } = get()
    const handler = stopHandlers[messageId]

    if (handler) {
      handler()
    }

    globalStopResponse()
  },

  setInputValue: (message: string) => set({ inputValue: message }),

  addDocToChatContext: (docId: string) =>
    set((state) => ({
      chatContextDocIds: state.chatContextDocIds.includes(docId)
        ? state.chatContextDocIds
        : [...state.chatContextDocIds, docId],
      chatOpenRequestNonce: state.chatOpenRequestNonce + 1,
    })),

  addDocsToChatContext: (docIds: string[]) =>
    set((state) => {
      const merged = [...state.chatContextDocIds]
      for (const id of docIds) {
        if (!merged.includes(id)) merged.push(id)
      }
      return {
        chatContextDocIds: merged,
        chatOpenRequestNonce: state.chatOpenRequestNonce + 1,
      }
    }),

  removeDocFromChatContext: (docId: string) =>
    set((state) => ({
      chatContextDocIds: state.chatContextDocIds.filter((id) => id !== docId),
    })),

  clearChatContext: () => set({ chatContextDocIds: [] }),

  requestOpenChat: () =>
    set((state) => ({ chatOpenRequestNonce: state.chatOpenRequestNonce + 1 })),

  resetChatState: () => set({ ...initialChatState }),

  setContextId: (context_id) => set({ context_id }),

  checkHasConversations: async () => {
    try {
      const { hasConversations } = await apiService.hasAnyConversations()
      set({ hasStartedChatting: hasConversations, isCheckingConversations: false })
    } catch (error) {
      console.error('Failed to check conversations:', error)
      // Default to hiding Getting Started panel on error to prevent incorrect display
      set({ hasStartedChatting: true, isCheckingConversations: false })
    }
  },
})
